import type Context from '@/context';
import type { IChipModel } from '@/interfaces/models';
import type { Challenge } from './definition';

export function isAuthorChip(challenge: Challenge, id: string) {
  return !!challenge.chipLibrary?.some(({ chip }) => chip.id === id);
}
export function canEditPlayerChip(context: Context, chip: IChipModel) {
  return (
    !!context.challengeAuthoring ||
    !context.challenge ||
    (!!context.challenge.playerChips &&
      !isAuthorChip(context.challenge, (context.app.getSourceChip?.(chip) ?? chip).id))
  );
}
export function assertPlayerDefinitionCapacity(context: Context, additional = 1) {
  const policy = context.challenge?.playerChips;
  if (!policy) throw Error('The challenge chip library is read-only.');
  const count = context.chipStore.chips.filter(
    (c) => c.id !== context.mainChip.id && !isAuthorChip(context.challenge!, c.id),
  ).length;
  if (count + additional > (policy.maxDefinitions ?? 256))
    throw Error(
      `Chip limit reached (${policy.maxDefinitions ?? 256}).${additional > 1 ? ` Import needs ${additional} slots, including dependencies.` : ''}`,
    );
  if (context.chipStore.chips.length + additional > 257)
    throw Error('The project supports at most 256 chip definitions.');
}

export function playerDefinitionUsage(context: Context) {
  const maximum = context.challenge?.playerChips?.maxDefinitions;
  const used = context.chipStore.chips.filter(
    (chip) =>
      chip.id !== context.mainChip.id && !isAuthorChip(context.challenge!, chip.id),
  ).length;
  return { used, maximum, atCap: maximum !== undefined && used >= maximum };
}

/** Legacy category switches also restricted placement. New switches only create. */
export function canCreatePlayerChip(policy: Challenge['playerChips'], kind?: string) {
  return (
    !!policy?.create && (kind === 'bundle-adapter' ? policy.bundleAdapters : policy.chips)
  );
}

export function updatePlayerCreation(
  challenge: Challenge,
  kind: 'chips' | 'bundleAdapters',
  enabled: boolean,
): Challenge {
  const old = challenge.playerChips;
  const chips = kind === 'chips' ? enabled : canCreatePlayerChip(old);
  const bundleAdapters =
    kind === 'bundleAdapters' ? enabled : canCreatePlayerChip(old, 'bundle-adapter');
  return {
    ...challenge,
    countingVersion: 3,
    scoring: challenge.scoring ? { ...challenge.scoring, metricsVersion: 3 } : undefined,
    // Preserve old author placement prohibitions as explicit per-chip rules.
    chipLibrary:
      old && old.permissionsVersion !== 2
        ? challenge.chipLibrary?.map((rule) => ({
            ...rule,
            available:
              rule.available &&
              (rule.chip.kind === 'bundle-adapter' ? old.bundleAdapters : old.chips),
          }))
        : challenge.chipLibrary,
    playerChips: {
      ...old,
      permissionsVersion: 2,
      chips,
      bundleAdapters,
      create: chips || bundleAdapters,
      import: false,
    },
  };
}
